import { Chip, Stack, Table, TableHead, TableRow, Typography } from '@mui/material';
import type { ShareInvoicesRequest } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import { shortenInvoiceId } from '@weft/services';
import type { Currency } from '@weft/types';
import { extract, isMultiple, type ItemOf } from '@weft/utils';
import { Price } from 'apps/app/src/components/design/Price';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { useShareInvoices } from 'apps/app/src/hooks/requests/use-share-invoices';
import { useDialog } from 'apps/app/src/hooks/use-dialog';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import { ShareInvoicesTableHeaderCell } from './ShareInvoicesTableHeaderCell';
import { ShareInvoicesTableCell } from './ShareInvoiceTableCell';

export type ShareInvoiceData = {
  invoiceId: string;
  customerName: string;
  contactEmail: string;
  issueDate: Date;
  amount: number;
  currency: Currency;
};

type ShareInvoicesDialogProps = {
  invoicesData: ShareInvoiceData[];
  children: React.ReactElement;
};

export const ShareInvoicesDialog: React.FC<ShareInvoicesDialogProps> = ({ invoicesData, children }) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const shareInvoiceMutation = useShareInvoices();
  const invoicesIds = extract(invoicesData, 'invoiceId');

  const handleShare = async () => {
    handleClose();
    
    const payload = invoicesIds.map<ItemOf<ShareInvoicesRequest>>((invoiceId) => ({ invoiceId }));
    await runMutation(shareInvoiceMutation, payload, 
      { 
        success: `Email${isMultiple(invoicesIds) ? 's' : ''} Sent!`,
        pending: `Sending Email${isMultiple(invoicesIds) ? 's' : ''}...`,
        error: `Failed to send Email${isMultiple(invoicesIds) ? 's' : ''}`,
      });
  };

  return (
    <>
      {React.cloneElement(children, { onClick: handleOpen })}
      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        cancelButtonText="Cancel"
        dialogContentProps={{ style: undefined, dividers: false }}
        dialogTitleProps={{ sx: { paddingX: '24px', paddingTop: '24px', paddingBottom: '0px' } }}
        ctaText={
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
          >
            <VyIcon icon='Send'/>
            <Typography
              fontWeight={500}
              fontSize="15px"
              lineHeight="26px"
            >
              Share
            </Typography>
          </Stack>
        }
        onSubmit={() => handleShare()}
        label={`Share ${invoicesIds.length} invoice${isMultiple(invoicesIds) ? 's' : ''}`}
        fullWidth
        PaperProps={{
          className: 'max-w-[780px]',
        }}
      >
        <Stack
          direction='column'
          width="100%"
          gap={2}
        >
          <Stack
            direction='row'
            alignItems="center"
            gap={1}
          >
            <Typography
              variant='subtitle1'
              color={theme => theme.palette.text.primary}
            >
              Invoices
            </Typography>
            <Chip
              size='small'
              label={invoicesIds.length}
            />
          </Stack>
          <Table
            width="100%"
            size='small'
          >
            <TableHead>
              <TableRow>
                <ShareInvoicesTableHeaderCell text='Invoice number'/>
                <ShareInvoicesTableHeaderCell text='Customer'/>
                <ShareInvoicesTableHeaderCell text='Email'/>
                <ShareInvoicesTableHeaderCell text='Issue Date'/>
                <ShareInvoicesTableHeaderCell text='Amount'/>
              </TableRow>
            </TableHead>
            {invoicesData.map(({ invoiceId, customerName, contactEmail, issueDate, amount, currency }) => (
              <TableRow
                key={invoiceId}
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, '& .MuiTableCell-sizeSmall': { paddingY: 2 } }}
              >
                <ShareInvoicesTableCell text={shortenInvoiceId(invoiceId)}/>
                <ShareInvoicesTableCell text={customerName}/>
                <ShareInvoicesTableCell text={contactEmail}/>
                <ShareInvoicesTableCell text={issueDate.toLocaleDateString()}/>
                <ShareInvoicesTableCell text={
                  <Price
                    variant='body1'
                    fontWeight={400}
                    fontSize="16px"
                    lineHeight="24px"
                    color={theme => theme.palette.text.primary}
                    currency={currency}
                  >
                    {amount}
                  </Price>
                }
                />
              </TableRow>
            ))}
          </Table>
        </Stack>
      </VyDialog>
    </>
  );
};